<div class="page">
    <div class="page-header">
        <div class="page-title-row">
            <h1 class="page-title">Part Coding</h1>
            <button
                type="button"
                class="help-button"
                aria-label="How to use Part Coding"
                (click)="helpPopover.toggle($event)">
                <i class="pi pi-question-circle"></i>
            </button>
            <p-popover
                #helpPopover
                styleClass="part-coding-help"
                appendTo="body">
                <div class="help-board">
                    <div>
                        <p class="help-kicker">Part Coding</p>
                        <ol class="help-steps">
                            <li>
                                <strong>Code it.</strong>
                                <ul>
                                    <li>Pick a location. Each row is Parent : Child.</li>
                                    <li>Paste the lines or upload one file, then click Code It.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Review all lines.</strong>
                                <ul>
                                    <li>Open a line to pick a different part. The status becomes Manual.</li>
                                    <li>Autos can stay as suggested. Review, RFQ, and Out of Stock each need a look.</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Export the batch.</strong>
                                <ul>
                                    <li>Export writes every line in view to the file, including a 100% match. That file is the part list for the NetSuite estimate.</li>
                                    <li>A 100% match is already confirmed history. It stays in the file and is not sent to the admin for another decision.</li>
                                    <li>The admin approves the lines that were sent. They can approve a selection at once.</li>
                                </ul>
                            </li>
                        </ol>
                    </div>
                    <div class="help-pair">
                        <div>
                            <p class="help-kicker">Status</p>
                            <ul class="help-notes">
                                <li class="help-success"><strong>Auto.</strong> A confident suggestion.</li>
                                <li class="help-warn"><strong>Review.</strong> A weaker suggestion, or a part found outside the market basket.</li>
                                <li class="help-danger"><strong>RFQ.</strong> No part was found.</li>
                                <li class="help-contrast"><strong>Out of Stock.</strong> The part has exactly 0 on hand. A dash means the quantity is unknown.</li>
                                <li class="help-info"><strong>Manual.</strong> You picked a different part. A line does not start this way.</li>
                            </ul>
                        </div>
                        <div>
                            <p class="help-kicker">Confidence</p>
                            <ul class="help-notes">
                                <li class="help-success"><strong>100%.</strong> The wording matches that customer’s history exactly.</li>
                                <li class="help-success"><strong>95–99%.</strong> Close to that history. Under 95% is not kept as history.</li>
                                <li class="help-success"><strong>75% and up.</strong> Auto, when the score is from the catalog.</li>
                                <li class="help-warn"><strong>55–74%.</strong> Review.</li>
                                <li class="help-danger"><strong>Under 55%.</strong> RFQ.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </p-popover>
            @if (rows().length) {
                <span class="page-subcount">{{ showingLabel() }}</span>
            }
        </div>

        <div class="user-chip">
            <span class="avatar">{{ initials(signedInUser()) }}</span>
            <span class="signed-in-name">{{ signedInUser() }}</span>
        </div>
    </div>

    <div class="table-card p-card">
        <div class="toolbar">
            <p-select
                styleClass="customer-select"
                panelStyleClass="part-coding-customer-panel"
                [options]="customers()"
                optionLabel="label"
                optionValue="id"
                [filter]="true"
                filterBy="label"
                filterPlaceholder="Search customers"
                [loading]="customersLoading()"
                [ngModel]="selectedCustomerId()"
                (ngModelChange)="onCustomerChange($event)"
                placeholder="Select a customer"
                appendTo="body"
                ariaLabel="Customer" />
            <p-select
                [options]="codingScopes"
                optionLabel="label"
                optionValue="id"
                [ngModel]="selectedScope()"
                (ngModelChange)="selectedScope.set($event)"
                ariaLabel="Scope" />
            <p-button
                label="Upload"
                [outlined]="true"
                [disabled]="isCoding()"
                (onClick)="uploadInput.click()" />
            <p-button
                label="Code It"
                [loading]="isCoding()"
                [disabled]="isCoding() || !canCode()"
                (onClick)="codeIt()" />
            <input
                #uploadInput
                type="file"
                [accept]="fileAccept"
                hidden
                (change)="onFileSelected($event)" />
            <span class="spacer"></span>
            <span class="source-label">{{ sourceLabel() }}</span>
            <p-button
                label="Export"
                [outlined]="true"
                (onClick)="exportRows()" />
        </div>

        @if (pendingFile(); as file) {
            <div class="pending-file">
                <span class="pending-file-name">{{ file.name }}</span>
                <span class="pending-file-size">{{ fileSizeLabel(file.size) }}</span>
                <button
                    type="button"
                    class="pending-file-remove"
                    aria-label="Remove file"
                    [disabled]="isCoding()"
                    (click)="clearPendingFile()">
                    ×
                </button>
            </div>
        }

        <div class="paste-wrap">
            <textarea
                class="paste-input"
                spellcheck="false"
                aria-label="Paste customer part descriptions"
                [ngModel]="pasteText()"
                (ngModelChange)="pasteText.set($event)"></textarea>
        </div>

        <div class="summary">
            <span class="total">Total: {{ rows().length }}</span>
            @for (status of statuses; track status) {
                <button
                    type="button"
                    class="status-pill"
                    [class]="'status-pill-' + statusSeverity(status)"
                    [attr.aria-pressed]="statusFilter().has(status)"
                    (click)="toggleStatusFilter(status)">
                    {{ status }} {{ statusCounts()[status] }}
                </button>
            }
            <input
                pInputText
                type="search"
                class="search-input"
                placeholder="Search lines…"
                aria-label="Search lines"
                [ngModel]="searchQuery()"
                (ngModelChange)="searchQuery.set($event)" />
            <span class="spacer"></span>
            @if (isCoding() && readingFile()) {
                <span class="processing">
                    <i class="pi pi-spin pi-spinner"></i>
                    {{ stageLabel() }} {{ fileClock() }}
                </span>
            } @else if (isCoding()) {
                <span class="processing">
                    <i class="pi pi-spin pi-spinner"></i>
                    {{ stageLabel() }} {{ formatMs(elapsedMs()) }}
                </span>
            } @else if (doneMs() !== null) {
                <span
                    class="processing done"
                    [pTooltip]="phaseTooltip()"
                    tooltipPosition="left"
                    tooltipStyleClass="phase-breakdown"
                    [tooltipDisabled]="phaseTimings().length === 0">
                    Done {{ formatMs(doneMs()!) }}
                </span>
            }
            @if (longFileHint(); as hint) {
                <span class="file-hint">{{ hint }}</span>
            }
        </div>

        @for (notice of uploadNotices(); track notice.text; let index = $index) {
            <div
                class="file-notice"
                [class.warn]="notice.severity === 'warn'"
                role="status">
                <span>{{ notice.text }}</span>
                <button
                    type="button"
                    class="file-notice-dismiss"
                    aria-label="Dismiss"
                    (click)="dismissUploadNotice(index)">
                    ×
                </button>
            </div>
        }

        <p-table
            [value]="filteredRows()"
            class="p-datatable-sm part-coding-table"
            [scrollable]="true"
            scrollHeight="flex"
            sortMode="single">
            <ng-template pTemplate="header">
                <tr>
                    <th
                        pSortableColumn="mark"
                        class="col-mark">
                        Mark
                        <p-sortIcon field="mark" />
                    </th>
                    <th pSortableColumn="status">
                        Status
                        <p-sortIcon field="status" />
                    </th>
                    <th
                        pSortableColumn="quantity"
                        class="col-qty">
                        Quantity
                        <p-sortIcon field="quantity" />
                    </th>
                    <th pSortableColumn="customerDescription">
                        Customer Description
                        <p-sortIcon field="customerDescription" />
                    </th>
                    <th pSortableColumn="partCode">
                        Part Code
                        <p-sortIcon field="partCode" />
                    </th>
                    <th pSortableColumn="rkDescription">
                        RK Description
                        <p-sortIcon field="rkDescription" />
                    </th>
                    <th
                        pSortableColumn="inventory"
                        class="col-inv">
                        RK Inv
                        <p-sortIcon field="inventory" />
                    </th>
                    <th class="col-mba">MBA</th>
                    <th pSortableColumn="source">
                        Source
                        <p-sortIcon field="source" />
                    </th>
                    <th
                        pSortableColumn="confidence"
                        class="col-conf">
                        Conf
                        <p-sortIcon field="confidence" />
                    </th>
                    <th class="col-review">Review</th>
                </tr>
            </ng-template>

            <ng-template
                pTemplate="body"
                let-row>
                <tr>
                    <td class="col-mark">{{ row.mark }}</td>
                    <td>
                        <p-tag
                            [value]="row.status"
                            [severity]="statusSeverity(row.status)" />
                    </td>
                    <td class="col-qty">{{ row.quantity }}</td>
                    <td class="col-cust-desc">{{ row.customerDescription }}</td>
                    <td>
                        @if (row.status === "RFQ") {
                            <span class="code">RFQ</span>
                        } @else if (row.status === "Review" && !row.partCode) {
                            <span class="dash">–</span>
                        } @else {
                            <span class="code">{{ row.partCode }}</span>
                        }
                    </td>
                    <td>
                        @if (row.status === "RFQ") {
                            <span class="placeholder">Insert RFQ description</span>
                        } @else if (row.status === "Review" && !row.partCode) {
                            <span class="dash">–</span>
                        } @else {
                            {{ row.rkDescription }}
                        }
                    </td>
                    <td class="col-inv">
                        @if (row.inventory === null) {
                            <span class="dash">–</span>
                        } @else {
                            <span [class.zero]="row.inventory === 0">{{ row.inventory }}</span>
                        }
                    </td>
                    <td class="col-mba">
                        @if (row.isMarketBasket === null) {
                            <span class="dash">–</span>
                        } @else {
                            <p-tag
                                [value]="row.isMarketBasket ? 'Yes' : 'No'"
                                [severity]="marketBasketSeverity(row.isMarketBasket)" />
                        }
                    </td>
                    <td>
                        @if (row.source) {
                            {{ row.source }}
                        } @else {
                            <span class="dash">–</span>
                        }
                    </td>
                    <td class="col-conf">
                        @if (row.status === "Review" && !row.partCode) {
                            <span class="dash">–</span>
                        } @else {
                            <p-tag
                                [value]="row.confidence + '%'"
                                [severity]="confidenceSeverity(row.confidence)" />
                        }
                    </td>
                    <td class="col-review">
                        <p-button
                            icon="pi pi-info-circle"
                            [text]="true"
                            [rounded]="true"
                            ariaLabel="Show match reason and candidates"
                            (onClick)="openCandidatePopover($event, row)" />
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
                <tr>
                    <td
                        [attr.colspan]="11"
                        class="empty-row">
                        @if (!rows().length) {
                            {{ lineCount() }} line{{ lineCount() === 1 ? "" : "s" }} ready above — choose
                            <strong>Code It</strong>
                            to match them against history, then the catalog.
                        } @else {
                            No lines match these filters.
                            <button
                                type="button"
                                class="link-button"
                                (click)="clearFilters()">
                                Clear filters
                            </button>
                        }
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>

<p-popover
    #candidatePopover
    appendTo="body"
    styleClass="candidate-popover"
    (onHide)="activeRow.set(null)">
    @if (activeRow(); as row) {
        <div class="popover-header">
            <h3>Candidates</h3>
            <span class="popover-sub">
                {{ candidates().length }} candidate{{ candidates().length === 1 ? "" : "s" }}
            </span>
        </div>

        <div class="candidate-head">
            <span class="col-candidate">Candidate</span>
            <span class="col-match">Match</span>
            <span class="col-inv">Inv</span>
        </div>
        <div class="candidate-list">
            @for (candidate of candidates(); track candidate.code) {
                <button
                    type="button"
                    class="candidate-row"
                    [class.selected]="selectedCandidateCode() === candidate.code"
                    (click)="selectedCandidateCode.set(candidate.code)">
                    <span class="candidate-main">
                        <span class="candidate-code-line">
                            <span class="candidate-code">{{ candidate.code }}</span>
                            @if (candidate.source) {
                                <span class="candidate-source">{{ candidate.source }}</span>
                            }
                        </span>
                        <span class="candidate-desc">{{ candidate.description }}</span>
                    </span>
                    <span class="candidate-metrics">
                        <span class="col-match">
                            <p-tag
                                [value]="candidate.matchPercent + '%'"
                                [severity]="confidenceSeverity(candidate.matchPercent)" />
                        </span>
                        <span
                            class="col-inv"
                            [class.zero]="candidate.inventory === 0">
                            {{ candidate.inventory }}
                        </span>
                    </span>
                </button>
            } @empty {
                <div class="candidate-empty">No candidates on file for this line.</div>
            }
        </div>

        <input
            pInputText
            type="search"
            class="candidate-search"
            placeholder="Search the catalog"
            aria-label="Search the catalog"
            [ngModel]="candidateFilter()"
            (ngModelChange)="onCandidateFilter($event)" />

        <label class="reason-label">
            Reason
            <textarea
                pTextarea
                class="candidate-reason"
                placeholder="Reason for picking"
                aria-label="Reason for picking"
                [ngModel]="reasonText()"
                (ngModelChange)="reasonText.set($event)"></textarea>
        </label>

        <div class="popover-footer">
            <p-button
                label="Confirm"
                (onClick)="confirmCandidate()" />
        </div>
    }
</p-popover>
